// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Badge from "@pacific/MobiusOverrides/Badge/Badge";
import { XBadgeModel } from "@pacific/XDL/types";
import { ellipsis, sharedBadgesContainerStyles } from "@pacific/XDL/Utilities";
import { mergeWithCss } from "@pacific/XDL/Utilities/mergeWithCss";
import * as React from "react";
import { FC, useState } from "react";
import { css } from "styled-components";

export interface OverlayBadgesProps {
    badges: XBadgeModel[] | null;
    wrapperCss: InjectableCss | undefined;
    variant?: "relative" | "absolute";
    maxLength?: number;
    extendedStyles?: OverlayBadgesStyle;
}

type Props = OverlayBadgesProps;

export interface OverlayBadgesStyle {
    relativeContainer?: InjectableCss;
    badgeText?: TypographyPresentationProps;
    badgeImageProps?: JSX.IntrinsicElements["img"];
}

const overlayBadgesStyles: OverlayBadgesStyle = {
    relativeContainer: {
        css: css`
            position: relative;
            ${sharedBadgesContainerStyles}
        `,
    },
    badgeText: {
        css: css`
            ${ellipsis(1)}
        `,
    },
};

const OverlayBadges: FC<Props> = ({ badges, wrapperCss, extendedStyles, variant = "absolute", maxLength }) => {
    const [styles] = useState(() => mergeWithCss(overlayBadgesStyles, extendedStyles));

    if (!badges || (typeof maxLength === "number" && !maxLength)) {
        return null;
    }

    const overlayBadges = maxLength ? badges.slice(0, maxLength) : badges;

    const renderedBadges = overlayBadges.map(overlayBadge => (
        <Badge
            badgeTag={overlayBadge.id}
            key={overlayBadge.id}
            type={overlayBadge.badgeType}
            value={overlayBadge.badgeType === "Image" ? overlayBadge.largeImageBadgePath : overlayBadge.displayText}
            badgeTextColor={overlayBadge.textColorHexCode}
            badgeBackgroundColor={overlayBadge.badgeColorHexCode}
            badgeTextRounding={overlayBadge.badgeStyle === "Round" ? 20 : 0}
            imageAltText={overlayBadge.imageAltText ? overlayBadge.imageAltText : overlayBadge.name}
            textBadgeCss={styles.badgeText}
            imageBadgeProps={styles.badgeImageProps}
            {...{ badgeBorderColor: overlayBadge.borderColor }}
        />
    ));

    if (variant === "relative") {
        return <StyledWrapper {...styles.relativeContainer}>{renderedBadges}</StyledWrapper>;
    }

    return <StyledWrapper {...wrapperCss}>{renderedBadges}</StyledWrapper>;
};

export default OverlayBadges;
